import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

// Standardized button variants with WCAG 2.1 AA compliant touch targets (44px minimum)
const journeyButtonVariants = cva(
  // Base styles - ensures minimum 44px touch target and accessibility
  "inline-flex items-center justify-center whitespace-nowrap rounded-xl text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 min-h-[44px] min-w-[44px] touch-manipulation select-none",
  {
    variants: {
      variant: {
        // Primary action - main CTA buttons
        primary: 
          "bg-red-600 text-white hover:bg-red-700 active:bg-red-800 shadow-md hover:shadow-lg active:shadow-sm transform hover:scale-[1.02] active:scale-[0.98]",
        
        // Secondary action - important but not primary
        secondary: 
          "bg-white text-red-600 border-2 border-red-600 hover:bg-red-50 active:bg-red-100 shadow-sm hover:shadow-md active:shadow-xs transform hover:scale-[1.02] active:scale-[0.98]",
        
        // Tertiary action - subtle actions
        tertiary: 
          "bg-gray-50 text-gray-700 hover:bg-gray-100 active:bg-gray-200 border border-gray-200 hover:border-gray-300 transform hover:scale-[1.02] active:scale-[0.98]",
        
        // Success actions - completion, progress
        success: 
          "bg-green-600 text-white hover:bg-green-700 active:bg-green-800 shadow-md hover:shadow-lg active:shadow-sm transform hover:scale-[1.02] active:scale-[0.98]",
        
        // Warning actions - caution required
        warning: 
          "bg-yellow-500 text-white hover:bg-yellow-600 active:bg-yellow-700 shadow-md hover:shadow-lg active:shadow-sm transform hover:scale-[1.02] active:scale-[0.98]",
        
        // Danger actions - destructive actions
        danger: 
          "bg-red-500 text-white hover:bg-red-600 active:bg-red-700 shadow-md hover:shadow-lg active:shadow-sm transform hover:scale-[1.02] active:scale-[0.98]",
        
        // Ghost - minimal visual impact
        ghost: 
          "text-gray-600 hover:bg-gray-100 active:bg-gray-200 hover:text-gray-900 transform hover:scale-[1.02] active:scale-[0.98]",
        
        // Link style
        link: 
          "text-red-600 underline-offset-4 hover:underline hover:text-red-700 active:text-red-800 p-0 h-auto min-h-[44px] flex items-center",
      },
      size: {
        // Touch-friendly sizes
        sm: "h-11 px-4 py-2 text-sm", // 44px minimum height
        md: "h-12 px-6 py-3 text-base", // 48px height for better touch
        lg: "h-14 px-8 py-4 text-lg", // 56px height for prominent actions
        xl: "h-16 px-10 py-5 text-xl", // 64px height for hero actions
        
        // Icon-only buttons
        icon: "h-11 w-11 p-0", // Square 44x44px
        iconLg: "h-12 w-12 p-0", // Square 48x48px
      },
      fullWidth: {
        true: "w-full",
        false: "",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
      fullWidth: false,
    },
  }
);

export interface JourneyButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof journeyButtonVariants> {
  asChild?: boolean;
  loading?: boolean;
  loadingText?: string;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
}

const JourneyButton = React.forwardRef<HTMLButtonElement, JourneyButtonProps>(
  ({ 
    className, 
    variant, 
    size, 
    fullWidth,
    asChild = false, 
    loading = false,
    loadingText,
    icon,
    iconPosition = "left",
    disabled,
    children,
    ...props 
  }, ref) => {
    const Comp = asChild ? Slot : "button";
    const isDisabled = disabled || loading;
    
    return (
      <Comp
        className={cn(journeyButtonVariants({ variant, size, fullWidth, className }))}
        ref={ref}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        {...props}
      >
        {loading && (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
        )}
        {!loading && icon && iconPosition === "left" && (
          <span className="mr-2" aria-hidden="true">{icon}</span>
        )}
        <span>
          {loading ? (loadingText || "Loading...") : children}
        </span>
        {!loading && icon && iconPosition === "right" && (
          <span className="ml-2" aria-hidden="true">{icon}</span>
        )}
      </Comp>
    );
  }
);
JourneyButton.displayName = "JourneyButton";

// Button group for related actions
export interface JourneyButtonGroupProps {
  className?: string;
  children: React.ReactNode;
  orientation?: "horizontal" | "vertical";
  spacing?: "tight" | "normal" | "loose";
}

const JourneyButtonGroup = React.forwardRef<HTMLDivElement, JourneyButtonGroupProps>(
  ({ className, children, orientation = "horizontal", spacing = "normal", ...props }, ref) => {
    const spacingClasses = {
      tight: orientation === "horizontal" ? "space-x-1" : "space-y-1",
      normal: orientation === "horizontal" ? "space-x-2" : "space-y-2", 
      loose: orientation === "horizontal" ? "space-x-4" : "space-y-4",
    };
    
    return (
      <div
        ref={ref}
        className={cn(
          "flex",
          orientation === "horizontal" ? "flex-row items-center" : "flex-col items-stretch",
          spacingClasses[spacing],
          className
        )}
        role="group"
        {...props}
      >
        {children}
      </div>
    );
  }
);
JourneyButtonGroup.displayName = "JourneyButtonGroup";

export { JourneyButton, JourneyButtonGroup, journeyButtonVariants };